import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createColumnHelper, RowData } from '@tanstack/react-table'

import { Chip, ChipProps, Stack, Tooltip, Typography } from '@mui/material'
import BadgeIcon from '@mui/icons-material/Badge'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'

import colors from '@/assets/scss/_themes-vars.module.scss'

import ChipButton from '@/components/ui/buttons/ChipButton'
import HeaderConfiguration from '@/components/ui/tables/HeaderConfiguration'
import Table, { TableProps } from '@/components/ui/tables/Table'

declare module '@tanstack/react-table' {
  interface TableMeta<TData extends RowData> {
    assignLicenses?: (staffId: string, licenses?: string[]) => void
  }
}

export type StaffData = {
  id: string
  name: string
  surname: string
  cps?: string
  email?: string
  status: 'PDS' | 'MEDECIN'
}

export type StaffsTableProps = Pick<TableProps<StaffData>, 'data' | 'loading' | 'onPaginationChange'> & {
  onAssignLicensesClick?: (staffId: string) => void
  onDeleteClick?: (staffId: string) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const columnHelper = createColumnHelper<StaffData>()

export function StaffsTable(props: StaffsTableProps) {
  const { data, loading, onAssignLicensesClick, onDeleteClick, onPaginationChange, totalCount } = props
  const { t } = useTranslation()

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('surname', {
        header: t('page.staffs.staffsTable.header.surname').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {getValue().toUpperCase()}
          </Typography>
        ),
      }),
      columnHelper.accessor('name', {
        header: t('page.staffs.staffsTable.header.name').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('email', {
        header: t('page.staffs.staffsTable.header.email').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.accessor('cps', {
        header: t('page.staffs.staffsTable.header.cps').toString(),
      }),
      columnHelper.accessor('status', {
        header: t('page.staffs.staffsTable.header.status').toString(),
        cell: ({ getValue }) => {
          const status = getValue()
          let color: ChipProps['color']
          switch (status) {
            case 'PDS':
              color = 'primary'
              break
            case 'MEDECIN':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={status} size="small" variant="filled" />
        },
      }),
      columnHelper.accessor('id', {
        header: t('page.staffs.staffsTable.header.id').toString(),
        cell: ({ getValue }) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {getValue()}
          </Typography>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: HeaderConfiguration,
        cell: ({ row, table }) => (
          <Stack justifyContent="center" direction="row" spacing={1}>
            <Tooltip title={t('table.staffs.actions.assignLicenses')}>
              <ChipButton onClick={() => table.options.meta?.assignLicenses?.(row.original.id)} data-testid={`assign-licenses-${row.original.id}`}>
                <BadgeIcon />
              </ChipButton>
            </Tooltip>
            <Tooltip title={t('common:button.delete.tooltip')}>
              <ChipButton
                color="error"
                data-testid={`delete-staff-${row.original.id}`}
                onClick={() => {
                  table.options.meta?.delete?.(row.original.id)
                }}
              >
                <DeleteRoundedIcon htmlColor="red" />
              </ChipButton>
            </Tooltip>
          </Stack>
        ),
        meta: {
          align: 'center',
          sxCell: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 1, background: '#FFF', minWidth: '50px' },
          sxHeader: { width: '50px', position: 'sticky', right: 0, borderLeft: '1px solid #efefef', zIndex: 10, background: '#f9fafb', minWidth: '50px' },
        },
      }),
    ]
  }, [t])

  return (
    <Table
      columns={columns}
      data={data}
      disableGlobalSearch
      loading={loading}
      manualPagination
      meta={{
        assignLicenses: onAssignLicensesClick,
        delete: onDeleteClick,
      }}
      onPaginationChange={onPaginationChange}
      totalRows={totalCount}
    />
  )
}

export default StaffsTable
